import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Container,
  Paper,
  Typography,
  Box,
  Chip,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Pagination,
  ToggleButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { Visibility, PersonAdd, DeleteOutline, Search } from '@mui/icons-material';
import { format } from 'date-fns';
import api from '../../utils/axios';
import Toast from '../../components/common/Toast';

const PAGE_SIZE = 10;

const MyKPIsPage = () => {
  const navigate = useNavigate();
  const { user } = useSelector(state => state.auth);
  const isCoach = user?.role === 'coach';
  const canAssign = isCoach || user?.role === 'manager' || user?.role === 'companyAdmin';
  const canDelete = isCoach || user?.role === 'superAdmin';
  const [kpis, setKpis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const [searchQuery, setSearchQuery] = useState('');
  const [templatesOnly, setTemplatesOnly] = useState(false);
  const [sortBy, setSortBy] = useState('date_newest');
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [page, setPage] = useState(1);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingKpi, setDeletingKpi] = useState(null);

  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assigningKpi, setAssigningKpi] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [assignCompany, setAssignCompany] = useState(null);
  const [assignUserId, setAssignUserId] = useState('');

  useEffect(() => {
    fetchKPIs();
    if (isCoach) {
      api.get('/companies').then(res => setCompanies(res.data)).catch(() => {});
    }
    if (canAssign) {
      api.get('/users').then(res => setAllUsers(res.data)).catch(() => {});
    }
  }, [isCoach, canAssign]);

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [searchQuery, templatesOnly, sortBy, selectedCompanyId]);

  const handleCloseToast = () => setToast(prev => ({ ...prev, open: false }));
  const showToast = (message, severity = 'success') => setToast({ open: true, message, severity });

  const fetchKPIs = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/kpi/my');
      setKpis(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch KPIs');
      showToast(err.response?.data?.message || 'Failed to fetch KPIs', 'error');
    } finally {
      setLoading(false);
    }
  };

  const openDeleteDialog = (kpi) => {
    setDeletingKpi(kpi);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/kpi/${deletingKpi._id}`);
      setKpis(prev => prev.filter(k => k._id !== deletingKpi._id));
      showToast('KPI deleted successfully');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to delete KPI', 'error');
    } finally {
      setDeleteDialogOpen(false);
      setDeletingKpi(null);
    }
  };

  const openAssignDialog = (kpi) => {
    setAssigningKpi(kpi);
    if (isCoach) {
      const currentCompanyId = kpi.companyId?._id || kpi.companyId;
      setAssignCompany(companies.find(c => c._id === currentCompanyId) || null);
    }
    setAssignUserId(kpi.isTemplate ? '' : (kpi.userId?._id || kpi.userId || ''));
    setAssignDialogOpen(true);
  };

  const handleAssign = async () => {
    try {
      const response = await api.patch(`/kpi/${assigningKpi._id}/assign`, {
        assignedUserId: assignUserId || undefined,
        companyId: assignCompany?._id,
      });
      if (assignUserId && assigningKpi.isTemplate) {
        setKpis(prev => [...prev, response.data]);
      } else {
        setKpis(prev => prev.map(k => k._id === assigningKpi._id ? response.data : k));
      }
      setAssignDialogOpen(false);
      showToast(assignUserId ? 'KPI assigned successfully' : 'KPI saved as template');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to assign KPI', 'error');
    }
  };

  const calculateKPIProgress = (areas) => {
    const totalTasks = areas.reduce((sum, area) => sum + area.tasks.length, 0);
    if (!totalTasks) return 0;
    const achievedTasks = areas.reduce((sum, area) =>
      sum + area.tasks.filter(task => task.status === 'achieved').length, 0);
    return Math.round((achievedTasks / totalTasks) * 100);
  };

  // Filter + sort
  const filtered = kpis
    .filter(kpi => {
      const matchesSearch = (kpi.name || '').toLowerCase().includes(searchQuery.toLowerCase());
      const matchesTemplate = templatesOnly ? kpi.isTemplate : true;
      const matchesCompany = selectedCompanyId
        ? (kpi.companyId?._id || kpi.companyId) === selectedCompanyId
        : true;
      return matchesSearch && matchesTemplate && matchesCompany;
    })
    .sort((a, b) => {
      if (sortBy === 'date_newest' || sortBy === 'date_oldest') {
        const aDate = a.reviewDate ? new Date(a.reviewDate) : null;
        const bDate = b.reviewDate ? new Date(b.reviewDate) : null;
        if (!aDate && !bDate) return 0;
        if (!aDate) return 1;
        if (!bDate) return -1;
        return sortBy === 'date_newest' ? bDate - aDate : aDate - bDate;
      }
      const aPct = calculateKPIProgress(a.areas);
      const bPct = calculateKPIProgress(b.areas);
      return sortBy === 'completion_high' ? bPct - aPct : aPct - bPct;
    });

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container>
      <Toast
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={handleCloseToast}
      />

      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h4" gutterBottom>
          My KPIs
        </Typography>

        {/* Toolbar */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search by name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ flex: 1, minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Sort by</InputLabel>
            <Select
              value={sortBy}
              label="Sort by"
              onChange={(e) => setSortBy(e.target.value)}
            >
              <MenuItem value="date_newest">Review Date — Newest</MenuItem>
              <MenuItem value="date_oldest">Review Date — Oldest</MenuItem>
              <MenuItem value="completion_high">Completion — Highest</MenuItem>
              <MenuItem value="completion_low">Completion — Lowest</MenuItem>
            </Select>
          </FormControl>
          {isCoach && companies.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Company</InputLabel>
              <Select
                value={selectedCompanyId}
                label="Company"
                onChange={(e) => setSelectedCompanyId(e.target.value)}
              >
                <MenuItem value="">All companies</MenuItem>
                {companies.map(c => (
                  <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {isCoach && (
            <ToggleButton
              value="templates"
              selected={templatesOnly}
              onChange={() => setTemplatesOnly(prev => !prev)}
              size="small"
              color="warning"
              sx={{ whiteSpace: 'nowrap' }}
            >
              Templates only
            </ToggleButton>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            {filtered.length} KPI{filtered.length !== 1 ? 's' : ''}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        {filtered.length === 0 ? (
          <Alert severity="info">
            {kpis.length === 0 ? "You haven't created any KPIs yet." : 'No KPIs match your search.'}
          </Alert>
        ) : (
          <>
            <List disablePadding>
              {paginated.map((kpi) => (
                <ListItem
                  key={kpi._id}
                  button
                  onClick={() => navigate(`/kpi/${kpi._id}`)}
                  sx={{
                    mb: 1,
                    py: 1,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    '&:hover': { backgroundColor: 'action.hover' },
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {kpi.name || 'Untitled KPI'}
                        </Typography>
                        {!kpi.isTemplate && (
                          <Chip
                            label={`${calculateKPIProgress(kpi.areas)}% Complete`}
                            color={calculateKPIProgress(kpi.areas) === 100 ? 'success' : 'primary'}
                            size="small"
                          />
                        )}
                        {kpi.isTemplate && (
                          <Chip label="Template" color="warning" size="small" />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 0.5 }}>
                        {canAssign && (
                          <>
                            {isCoach && (
                              <Typography variant="caption" color="text.secondary">
                                Company: {kpi.companyId?.name || '—'}
                              </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary">
                              Assigned to: {kpi.isTemplate ? 'Unassigned' : kpi.userId?.name || '—'}
                            </Typography>
                            {!kpi.isTemplate && kpi.userId?.reportsTo && (
                              <Typography variant="caption" color="text.secondary">
                                Reporting to: {kpi.userId.reportsTo.name}
                              </Typography>
                            )}
                          </>
                        )}
                        <Typography variant="caption" color="text.secondary">
                          Review: {kpi.reviewDate ? format(new Date(kpi.reviewDate), 'PP') : 'Not set'}
                        </Typography>
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {canAssign && (kpi.createdBy === user?._id || kpi.createdBy?._id === user?._id) && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PersonAdd />}
                        onClick={(e) => { e.stopPropagation(); openAssignDialog(kpi); }}
                      >
                        Assign
                      </Button>
                    )}
                    {canDelete && (kpi.createdBy === user?._id || kpi.createdBy?._id === user?._id) && (
                      <IconButton
                        edge="end"
                        color="error"
                        onClick={(e) => { e.stopPropagation(); openDeleteDialog(kpi); }}
                      >
                        <DeleteOutline />
                      </IconButton>
                    )}
                    <IconButton edge="end" onClick={() => navigate(`/kpi/${kpi._id}`)}>
                      <Visibility />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>

            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, value) => setPage(value)}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </Paper>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete KPI</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{deletingKpi?.name || 'this KPI'}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign KPI to User</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 3, pb: 3 }}>
            {isCoach && (
              <Autocomplete
                options={companies}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option._id === value._id}
                value={assignCompany}
                onChange={(_, newValue) => { setAssignCompany(newValue); setAssignUserId(''); }}
                renderInput={(params) => <TextField {...params} label="Company" />}
              />
            )}
            <Autocomplete
              options={isCoach
                ? (assignCompany ? allUsers.filter(u => (u.company?._id || u.company) === assignCompany._id) : [])
                : allUsers}
              getOptionLabel={(option) => `${option.name} (${option.role})`}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              value={allUsers.find(u => u._id === assignUserId) || null}
              onChange={(_, newValue) => setAssignUserId(newValue?._id || '')}
              disabled={isCoach && !assignCompany}
              renderInput={(params) => <TextField {...params} label="Assign to User (optional)" />}
              noOptionsText={isCoach && !assignCompany ? 'Select a company first' : 'No users available'}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAssign}>Save</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MyKPIsPage;
